import { _, helpers, mui, React, ts, ui } from '_core';

import { rebalanceValidation, transform } from './helpers';
import { Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  portfolios: string;
  fullScreen: boolean;
  hasBenchmark: boolean;
  frequency: ts.enums.FREQUENCY_ENUM;
  params: Params;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  title: string;
  isBasket: boolean;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  portfolios,
  hasBenchmark = false,
  params,
  fullScreen,
  frequency,
  title,
  isBasket,
}: ChartProps) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [selectedTab, setSelectedTab] = React.useState<'ANNUAL' | 'MONTHLY'>('ANNUAL');

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: portfolios,
          query: ['$and', ['statistic', 'equals', params.type], ['frequency', 'equals', selectedTab]],
          columns: ['date', 'portfolio', 'active'],
        },
      });

      if (response) {
        const resp = response;
        setData(resp.data);
      }
    } catch (err) {
      setError(JSON.stringify(err));
    }
  };

  React.useEffect(() => {
    if (portfolios) loadData();
    return () => setData(null);
  }, [portfolios, selectedTab]);

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    return transform(nestedBarChartFormat(data, 'date'), hasBenchmark, isBasket);
  }, [data, hasBenchmark, isBasket]);

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      <mui.core.Tabs
        value={selectedTab}
        indicatorColor="primary"
        textColor="primary"
        onChange={(e, newTab) => setSelectedTab(newTab)}
        aria-label="Return Horizon Tabs"
      >
        <mui.core.Tab label="Annual" value="ANNUAL" />
        {rebalanceValidation('MONTHLY', frequency, params.type) && <mui.core.Tab label="Monthly" value="MONTHLY" />}
        {rebalanceValidation('WEEKLY', frequency, params.type) && <mui.core.Tab label="Weekly" value="WEEKLY" />}
      </mui.core.Tabs>
      <mui.core.Box flexGrow={1}>
        <ui.NestedBarChart
          id={id}
          data={formattedData?.data}
          count={formattedData?.count}
          rotation={selectedTab != 'ANNUAL' ? 90 : 0}
          category="category"
          numberFormat={params.type !== 'adjusted' ? '#.##%' : '#.##'}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          fullScreen={fullScreen}
          exportTitle={title}
          readOnly={readOnly}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
